/**
 * The registry of the close guards of the panels with unsaved work, and the requests that ask
 * them before a departure or a close.
 */

import type { Unsubscribe } from '@/types/common';
import type {
    DeferredDeparture, DepartureIntent, OpenPanel, PanelCloseGuard, PanelId,
} from '@/types/panels';

import { getSummonedDependents } from './panelSummons';

/**
 * True when a close guard took the transition over instead of completing it.
 * Every shell entry point answers either with the state it produced or with
 * this, so one predicate reads them all.
 */
export function isDeferred(transition: unknown): transition is DeferredDeparture {
    return typeof transition === 'object' && transition !== null &&
        'deferred' in transition && transition.deferred === true;
}

const guards = new Map<string, PanelCloseGuard>();

export function registerPanelCloseGuard(
    panelId: string, guard: PanelCloseGuard,
): Unsubscribe {
    guards.set(panelId, guard);
    return () => {
        if (guards.get(panelId) === guard) {
            guards.delete(panelId);
        }
    };
}

/** `fn`, but only the first call runs it; each later call gives the first result. */
function once<T>(fn: () => T): () => T {
    // Boxed, so "has it run" and "what it returned" are one value and the
    // result needs no cast back to T.
    let outcome: { result: T } | null = null;
    return (): T => {
        if (outcome === null) {
            outcome = { result: fn() };
        }
        return outcome.result;
    };
}

export function requestPanelDeparture<T>(
    panelIds: string[], intent: DepartureIntent, runTransition: () => T,
): DeferredDeparture | T {
    const finish = once(runTransition);
    // Each panel with unsaved work asks in its turn: its resume asks the next panel. Thus, a
    // cancel in any panel keeps all of them.
    const askFrom = (start: number): DeferredDeparture | T => {
        for (const [offset, panelId] of panelIds.slice(start).entries()) {
            const guard = guards.get(panelId);
            if (guard?.hasUnsavedWork()) {
                guard.defer({ ...intent, resume: once(() => askFrom(start + offset + 1)) });
                return { deferred: true, kind: intent.kind };
            }
        }
        return finish();
    };
    return askFrom(0);
}

/** Whether an open panel holds work that a page reload would lose. */
export function hasUnsavedWork(): boolean {
    return [...guards.values()].some((guard) => guard.hasUnsavedWork());
}

/** Whether one of `panelIds` has unsaved work. It asks the user nothing. */
export function doPanelsHaveUnsavedWork(panelIds: readonly string[]): boolean {
    return panelIds.some((panelId) => guards.get(panelId)?.hasUnsavedWork() ?? false);
}

/** Closes `panelId` after the guards of the panel and of the panels it summoned agree. */
export function requestPanelClose<T>(
    closePanel: (id: PanelId) => T, panelId: PanelId, openPanels: OpenPanel[],
): DeferredDeparture | T {
    const departingPanelIds = [panelId, ...getSummonedDependents(openPanels,
        new Set([panelId]), null)];
    return requestPanelDeparture(departingPanelIds, { kind: 'close' }, () => closePanel(panelId));
}

export function resetPanelCloseGuardsForTests(): void {
    guards.clear();
}
